import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../auth/useAuth';
import useIsMobile from '../auth/useIsMobile';
import { listTimesheets } from '../services/timesheets';
import { listTeam } from '../services/team';

/**
 * TimesheetsLayout
 * -----------------
 * Wraps the three Timesheets tabs (Submit / Approvals / Adjustments)
 * under a single `/timesheets` route hierarchy. The previous setup had
 * two top-level sidebar items ("Timesheets" and "Timesheet Approvals")
 * pointing at distinct pages — this collapses both into one section
 * with a tab strip the RM/Director can flip between without losing
 * filter state, while still letting deep links like
 * `/timesheets/submit?edit=123` and `/timesheets/submit?supplement-of=456`
 * work.
 *
 * Routing layout (see App.jsx):
 *   /timesheets                  → role-aware redirect (this component)
 *   /timesheets/submit           → the form (was /timesheets)
 *   /timesheets/approvals        → review dashboard (everything)
 *
 * Role-aware default tab:
 *   Liaison (submitter-only)     → Submit
 *   Regional Manager / Director  → Approvals
 *
 * Pending-approval badge:
 *   The Approvals tab shows a small pill with the number of Pending
 *   Approval timesheets (regular + supplements + credits — every
 *   record an approver needs to act on). A lightweight listTimesheets
 *   call runs on mount and again whenever the route inside the layout
 *   changes — cheap way to keep the badge fresh after Approve/Reject
 *   actions without setting up cross-component state. If the fetch
 *   fails (no permission, offline, etc.) the badge just doesn't
 *   render.
 */
export default function TimesheetsLayout() {
  const {
    isDirector,
    isRegionalManager,
    isExecutive,
    isLiaisonManager,
    name,
    email,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const isApprover = isDirector || isRegionalManager;

  // Bare /timesheets bounces to the role-appropriate tab. We use
  // replace:true so the browser back button doesn't trap the user on
  // an empty parent route. On phones, Approvals is hidden, so everyone
  // — including approvers — lands on Submit.
  useEffect(() => {
    if (location.pathname === '/timesheets' || location.pathname === '/timesheets/') {
      const target =
        isApprover && !isMobile ? '/timesheets/approvals' : '/timesheets/submit';
      navigate(target, { replace: true });
    }
  }, [location.pathname, isApprover, isMobile, navigate]);

  // ---- Pending-approval badge ---------------------------------------
  // Counts non-adjustment timesheets in Pending Approval, scoped the
  // SAME way the Approvals page scopes its inbox so the badge number
  // always matches what the list shows (July 2026 fix — the badge used
  // to count everything, so an RM could see "1" with an empty list):
  //   • Director → every pending timesheet
  //   • RM       → pending timesheets whose stored RegionalManager is
  //                them, plus their OWN submissions (an RM has no RM
  //                above them, so their rows carry no RegionalManager;
  //                they approve those themselves).
  // Empty until the fetch resolves so we don't flash a "0" badge.
  const [pendingCount, setPendingCount] = useState(null);
  useEffect(() => {
    if (!isApprover) {
      setPendingCount(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await listTimesheets();
        const normName = (name || '').trim().toLowerCase();
        // Resolve the RM's own team-record id (by email) so their own
        // submissions match by SubmitterId even after a rename. Name
        // matching stays as the fallback for legacy rows.
        let myTeamId = '';
        if (!isDirector && isRegionalManager) {
          try {
            const team = await listTeam();
            const normEmail = (email || '').trim().toLowerCase();
            const mine = (team || []).find(
              (m) => (m.Email || '').trim().toLowerCase() === normEmail,
            );
            myTeamId = mine ? String(mine.id) : '';
          } catch {
            /* non-fatal — fall back to name-only matching */
          }
        }
        if (cancelled) return;
        let n = 0;
        for (const t of rows || []) {
          if ((t.Status || '') !== 'Pending Approval') continue;
          if (!isDirector && isRegionalManager) {
            const rm = (t.RegionalManager || '').trim().toLowerCase();
            const own =
              (myTeamId && String(t.SubmitterId || '').trim() === myTeamId) ||
              (t.SubmitterName || '').trim().toLowerCase() === normName;
            if (rm !== normName && !own) continue;
          }
          n += 1;
        }
        setPendingCount(n);
      } catch {
        if (!cancelled) setPendingCount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // location.pathname change after Approve/Reject (the action
    // navigates back to /timesheets/approvals) triggers a refresh.
  }, [isApprover, isDirector, isRegionalManager, name, email, location.pathname]);

  const tabs = useMemo(() => {
    const list = [{ to: '/timesheets/submit', label: 'Submit New Tracker' }];
    // Approvals and Adjustments are desk-bound RM/Director workflows, so
    // they're hidden on phone widths to keep the mobile experience focused
    // on submitting hours on the go. The routes still resolve via a direct
    // link if an approver needs one.
    if (!isMobile) {
      // Approvals tab — Directors and RMs (who act on approvals) plus
      // Executives (read-only per the roles spec). Hidden entirely for
      // Liaison Managers and Liaisons (per Jillian, July 2026) — they
      // have no business on that page, so no tab, no badge.
      if (isApprover || isExecutive) {
        list.push({
          to: '/timesheets/approvals',
          label: 'Approvals',
          badge: pendingCount && pendingCount > 0 ? pendingCount : null,
        });
      }
      // Adjustments is DIRECTOR-ONLY — the central place to raise a
      // supplement or credit for a supplier and plant. Regional Managers
      // no longer see this tab (per Jillian, Aug 2026); only Directors
      // can create adjustments. (Briefly opened to Liaison Managers on
      // July 22, 2026 and reverted the same day.)
      if (isDirector) {
        list.push({ to: '/timesheets/adjustments', label: 'Adjustments' });
      }
      // Approved Trackers — Liaison Managers only (per Jillian, July
      // 2026): a read-only, search-and-sort view of their OWN approved
      // Billing Trackers. Directors / RMs already get this in the
      // Approvals page's Approved section, so it's not added for them.
      if (isLiaisonManager && !isApprover) {
        list.push({ to: '/timesheets/approved', label: 'Approved Trackers' });
      }
    }
    return list;
  }, [pendingCount, isApprover, isDirector, isExecutive, isLiaisonManager, isMobile]);

  return (
    <>
      {/* Tab strip — sits above each child page's page-header so
          the child pages can keep their own contextual h1 ("Weekly
          Timesheets", "Edit Submitted Timesheet", "Add Supplement
          to...") without the layout fighting for the title. */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: '1px solid var(--brand-border, #e5e7eb)',
          marginBottom: 16,
        }}
      >
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={false}
            style={({ isActive }) => ({
              padding: '10px 14px',
              fontSize: 14,
              fontWeight: 500,
              color: isActive ? 'var(--brand-ink)' : 'var(--brand-muted)',
              borderBottom: isActive
                ? '2px solid var(--brand-primary)'
                : '2px solid transparent',
              textDecoration: 'none',
              marginBottom: -1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            })}
          >
            {t.label}
            {t.badge != null && (
              <span
                style={{
                  background: 'var(--brand-primary-soft, #fbe6d5)',
                  color: 'var(--brand-primary-dark, #c86313)',
                  fontSize: 11,
                  padding: '1px 7px',
                  borderRadius: 999,
                  fontWeight: 600,
                }}
              >
                {t.badge}
              </span>
            )}
          </NavLink>
        ))}
      </div>

      <Outlet />
    </>
  );
}
